import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { styled } from "@mui/material/styles";
import { Typography, Box, Button, TextField, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useResponsive from "../hooks/useResponsive";
import Logo from "../components/Logo";
import SeverErrorIllustration from "../components/SeverErrorIllustration";
import { resendVerificationEmail } from "../AuthenticationData.ts";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",

  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const StyledSection = styled("div")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    width: "40%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledContent = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: theme.spacing(8, 2, 2, 2),
  overflowY: "auto",
  [theme.breakpoints.up("md")]: {
    width: "35%",
    alignItems: "center",
    padding: theme.spacing(3),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 480,
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6, 5),
    boxShadow: theme.shadows[8],
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const verifyHeadings = [
  "Just one more step!",
  "Let's get you verified.",
  "Check your inbox!",
  "Almost there...",
];

const verifySubheadings = [
  "A verification link has been sent to your email address.",
  "Click the link we sent to complete your registration.",
  "Don't forget to check your spam folder just in case.",
];

const getRandomMessages = () => {
  const heading =
    verifyHeadings[Math.floor(Math.random() * verifyHeadings.length)];
  const subheading =
    verifySubheadings[Math.floor(Math.random() * verifySubheadings.length)];
  return { heading, subheading };
};

export default function VerifyMail() {
  const navigate = useNavigate();
  const mdUp = useResponsive("up", "md");

  const [email, setEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [welcomeMessages] = useState(getRandomMessages);

  const [emailError, setEmailError] = useState("");

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (newEmail.trim() !== "" && !emailRegex.test(newEmail)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleResendEmail = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email address", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    setIsResending(true);
    try {
      const response = await resendVerificationEmail(email);
      if (response.error) {
        toast.error(response.error, {
          position: "top-center",
          autoClose: 3000,
        });
        setEmail("");
        setEmailError("");
      } else {
        toast.success(
          "A new verification link has been sent. Please check your inbox.",
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
        setEmail("");
        setEmailError("");
      }
    } catch (error) {
      toast.error("An error occurred. Please wait a moment and try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contactie | Verify Email</title>
        <meta
          name="description"
          content="Complete your Contactie account registration by verifying your email address."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <StyledPageContainer>
        <ContentWrapper>
          <Box
            component="header"
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              zIndex: 10,
              top: 40,
              left: 40,
            }}
          >
            <Logo sx={{ height: 30, width: "auto" }} />
          </Box>

          {mdUp && (
            <StyledSection>
              <Typography
                variant="h4"
                sx={{ textAlign: "center", mb: 2, px: 5 }}
              >
                {welcomeMessages.heading}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", textAlign: "center" }}
              >
                {welcomeMessages.subheading}
              </Typography>
              <SeverErrorIllustration
                sx={{
                  width: "100%",
                  maxWidth: 320,
                  maxHeight: "50vh",
                  objectFit: "contain",
                  mt: 8,
                }}
              />
            </StyledSection>
          )}

          <StyledContent>
            <FormContainer>
              <Box
                sx={{
                  mb: 5,
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Logo sx={{ height: 35, width: "auto", mb: 3 }} />
                <Typography variant="h4" gutterBottom>
                  {welcomeMessages.heading}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 3 }}
                >
                  Didn't receive the email? Check spam or resend it below.
                </Typography>
                <SeverErrorIllustration sx={{ height: 180 }} />{" "}
              </Box>

              <Box sx={{ display: { xs: "none", md: "block" }, mb: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  Verify Your Email
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  Didn't receive the email? Check your spam folder or resend the
                  link.
                </Typography>
              </Box>

              <Box sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  label="Enter your email to resend the link"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  error={!!emailError}
                  helperText={emailError}
                  sx={{ mb: 3 }}
                />
                <Stack spacing={2}>
                  <Button
                    fullWidth
                    size="large"
                    variant="outlined"
                    onClick={handleResendEmail}
                    disabled={isResending || !email.trim() || !!emailError}
                  >
                    {isResending ? "Sending..." : "Resend Verification Email"}
                  </Button>
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={() => navigate("/Login")}
                    disabled={isResending}
                  >
                    Go to Login Page
                  </Button>
                </Stack>
              </Box>
            </FormContainer>
          </StyledContent>
        </ContentWrapper>
        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
